import cn from 'clsx';
import { cva, type VariantProps } from 'class-variance-authority';
import { getLifestyleCategoryTranslationKey } from '@pkgs/components/utils/translationKeyUtils';
import { type WithTestId } from '@pkgs/components/types/types';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { Headline } from '@ev-tech/ui-core-components/Headline';
import { getKeys } from '@ev-tech/search-modules-api';

import { Checkbox } from '../Checkbox/Checkbox';

import styles from './LifestyleFilter.module.css';

const lifestyleListVariants = cva(styles.list, {
  variants: {
    isGrid: {
      true: styles.grid,
      false: null,
    },
  },
  defaultVariants: {
    isGrid: false,
  },
});

type LifestyleFilterProps = WithTestId<{
  className?: string;
  hasTitle?: boolean;
  isGrid?: NonNullable<VariantProps<typeof lifestyleListVariants>['isGrid']>;
}>;

export const LifestyleFilter = ({ className, hasTitle = false, isGrid = false, ...props }: LifestyleFilterProps) => {
  const { t } = useAppContext();

  const lifestyleCategories = useSearchStore((state) => state.lifestyleCategories);
  const setLifestyleCategory = useSearchStore((state) => state.setLifestyleCategory);

  return (
    <div className={cn(styles.container, className)} data-testid="search-components_lifestyle-filter" {...props}>
      {hasTitle && <Headline label={t('search.searchbar.lifestyle')} size="h6" />}
      <div className={lifestyleListVariants({ isGrid })}>
        {getKeys(lifestyleCategories).map((category) => {
          const lifestyleCategoryTranslationKey = getLifestyleCategoryTranslationKey(category);
          const label = lifestyleCategoryTranslationKey ? t(lifestyleCategoryTranslationKey) : category;
          return (
            <Checkbox
              key={category}
              value={category}
              label={label}
              aria-label={label}
              isSelected={lifestyleCategories[category]}
              onChange={(isSelected) => setLifestyleCategory(category, isSelected)}
            />
          );
        })}
      </div>
    </div>
  );
};
